/**
 * Workflow-run completion notifier — fires a desktop notification +
 * optional sound when a queued/running workflow transitions to a terminal
 * state (succeeded / failed / cancelled).
 *
 * Independent from the /workflows page polling so the user gets feedback
 * even when looking at a different page (e.g. the agent drawer just
 * proposed a backup-restore; the user is browsing /sites while it runs).
 *
 * Implementation:
 *   - Polls /api/workflows/runs/ every 6 seconds when the tab is visible.
 *   - Tracks the last-known state of every run id seen this session.
 *   - On a non-terminal → terminal transition, fires the notification.
 *   - First-time priming (when the session boots and the run is already
 *     terminal) does NOT fire — we don't want to re-notify history.
 *
 * Prefs are persisted in localStorage so a user who enabled sound once
 * keeps it across reloads.
 *
 * Browser quirks:
 *   - Notification.requestPermission() must be triggered from a user
 *     gesture (button click). The store exposes a request action that the
 *     bell-prefs UI invokes synchronously inside the click handler.
 *   - WebAudio's AudioContext is gated behind a user gesture too. We
 *     create the context lazily on the first beep request.
 */
import { useEffect, useRef } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { get as apiGet } from "@/api/client";
import type { WorkflowRun } from "@/api/schemas/workflows";
import { useAuth } from "@/stores/auth";

const TERMINAL_STATES = new Set<string>(["success", "failed", "cancelled"]);

/** ms between polls when the tab is visible. The /workflows page polls at
 *  the same cadence; pulling /workflows/runs/ is one cheap SQL query. */
const POLL_INTERVAL_MS = 6_000;

interface State {
  /** Master switch — when false, the watcher loop short-circuits. */
  enabled: boolean;
  /** Play a short beep on completion. */
  soundEnabled: boolean;
  /** Show a Notification API toast on completion. Requires browser
   *  permission; when permission is denied this stays false even if the
   *  user toggled it on. */
  desktopEnabled: boolean;

  setEnabled: (v: boolean) => void;
  setSoundEnabled: (v: boolean) => void;
  /** Toggle desktop on/off. When turning on without permission, also
   *  triggers the browser's native permission prompt. */
  toggleDesktop: () => Promise<void>;
}

export const useRunNotifier = create<State>()(
  persist(
    (set, get) => ({
      enabled: true,
      soundEnabled: true,
      desktopEnabled: false,

      setEnabled: (v) => set({ enabled: v }),
      setSoundEnabled: (v) => set({ soundEnabled: v }),

      toggleDesktop: async () => {
        const { desktopEnabled } = get();
        if (desktopEnabled) {
          set({ desktopEnabled: false });
          return;
        }
        // Turning on — make sure the browser actually allows it.
        if (typeof window === "undefined" || !("Notification" in window)) {
          set({ desktopEnabled: false });
          return;
        }
        let perm = Notification.permission;
        if (perm === "default") {
          try {
            perm = await Notification.requestPermission();
          } catch {
            perm = "denied";
          }
        }
        set({ desktopEnabled: perm === "granted" });
      },
    }),
    {
      name: "opus.run-notifier",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        enabled: s.enabled,
        soundEnabled: s.soundEnabled,
        desktopEnabled: s.desktopEnabled,
      }) as Pick<State, "enabled" | "soundEnabled" | "desktopEnabled">,
    },
  ),
);

/* ---------- audio (lazy AudioContext) ---------- */

let _audioCtx: AudioContext | null = null;

/** Lazy AudioContext — most browsers block creation outside a user
 *  gesture. By the time we play a beep the user has already interacted
 *  with the page (they clicked something to trigger the workflow), so
 *  the context will be allowed. */
function _getAudioCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  const Ctx: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext; }).webkitAudioContext;
  if (!Ctx) return null;
  _audioCtx = new Ctx();
  return _audioCtx;
}

/** Two-tone success / failure beep. Different pitches so the user can
 *  hear what happened without looking. */
function _playBeep(ok: boolean): void {
  const ctx = _getAudioCtx();
  if (!ctx) return;
  // ok → 880Hz (mint-y), bad → 220Hz (rose-y).
  const freq = ok ? 880 : 220;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  // Quick attack/release so it's a "blip", not a tone.
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
  gain.gain.linearRampToValueAtTime(0, now + 0.18);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.20);
}

/* ---------- watcher hook ---------- */

/** Poll the workflow-runs endpoint and notify on completion transitions.
 *  Mount once at the app root. Returns nothing — this is a fire-and-forget
 *  side-effect hook.
 *
 *  Implementation note: we keep last-known state in a ref-like Map outside
 *  React state because every change would re-render every consumer of
 *  useRunNotifier (the prefs UI) for nothing. The watcher is the sole
 *  reader/writer of this map. */
export function useRunNotifierWatcher(): void {
  // Late import to keep this module hook-free at load time (avoids
  // cycles in tests). React is required by the consuming hook though.
  const enabled = useRunNotifier((s) => s.enabled);
  const soundEnabled = useRunNotifier((s) => s.soundEnabled);
  const desktopEnabled = useRunNotifier((s) => s.desktopEnabled);
  const authStatus = useAuth((s) => s.status);
  const user = useAuth((s) => s.user);

  // Last-known state per run id, kept outside React state so the prefs
  // UI doesn't re-render on every tick. Sole reader/writer is this hook.
  const lastSeen = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const onLoginPage = path === "/login";
    const canPoll = enabled && authStatus === "ready" && !!user && !onLoginPage;
    if (!canPoll) return;

    // Fresh session window: avoid comparing against stale ids from a
    // previous user or a pre-login state.
    lastSeen.current?.clear();

    let cancelled = false;
    let visible = !document.hidden;
    let firstPass = true;
    const closeTimers = new Set<ReturnType<typeof setTimeout>>();

    const tick = async () => {
      if (cancelled || !visible) return;
      try {
        const runs = await apiGet<WorkflowRun[]>("/workflows/runs/");
        if (cancelled) return;
        for (const run of runs) {
          const prev = lastSeen.current!.get(run.id);
          lastSeen.current!.set(run.id, run.state);
          // First pass: prime the map without firing — we don't want to
          // re-notify a user about runs that finished before they opened
          // the tab.
          if (firstPass) continue;
          // Notify when state newly enters a terminal status. Skip if we
          // already saw that exact state (idempotent).
          if (prev && prev !== run.state && TERMINAL_STATES.has(run.state)) {
            const ok = run.state === "success";
            const verb = run.state === "success"
              ? "completed"
              : run.state === "cancelled"
                ? "was cancelled"
                : "failed";
            // Desktop toast — silently dropped if permission lapsed.
            if (desktopEnabled && typeof Notification !== "undefined" && Notification.permission === "granted") {
              try {
                const n = new Notification(`Workflow ${verb}`, {
                  body: `${run.workflowName} → ${run.target}`,
                  icon: "/favicon.svg",
                  // Same tag = browser de-dupes if a flurry happens.
                  tag: `opus-run-${run.id}`,
                });
                // Auto-close after 8s — modern browsers ignore close()
                // for some types but it's harmless.
                const closeTimer = setTimeout(() => {
                  closeTimers.delete(closeTimer);
                  try { n.close(); } catch { /* ignore */ }
                }, 8_000);
                closeTimers.add(closeTimer);
              } catch {
                // Notification constructor throws on Safari iOS — silent.
              }
            }
            if (soundEnabled) _playBeep(ok);
          }
        }
        firstPass = false;
      } catch {
        // Quiet — failed polls are common (401, timeout). The next tick will
        // try again.
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
    const onVis = () => { visible = !document.hidden; };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      closeTimers.forEach((t) => clearTimeout(t));
      closeTimers.clear();
    };
    // The watcher restarts on prefs change so toggling desktopEnabled
    // takes effect on the next tick instead of waiting for a navigation.
  }, [enabled, soundEnabled, desktopEnabled, authStatus, user]);
}
